import Joi from 'joi';

export const validateField = (name, value, schemaData) => {
  const fieldSchema = Joi.object({ [name]: schemaData.extract(name) });
  const { error } = fieldSchema.validate({ [name]: value });
  return error ? error.details[0].message : null;
};

export const validateProcedureField = (index, name, value, schema, formValues) => {
  const schemaForIndex = Joi.object({
      procedure: Joi.string().required().label('Procedure'),
      bodyParts: Joi.string().required().label('Body Parts'),
      bodySide: Joi.string().required().label('Body Side'),
      diagnosisRuleOut1: Joi.string().required().label('Diagnosis / Rule Out 1'),
      diagnosisRuleOut2: Joi.string().required().label('Diagnosis / Rule Out 2')
  });

  const currentItem = { ...formValues[index], [name]: value };
  const { error } = schemaForIndex.validate(currentItem, { abortEarly: false });
  
  if (error) {
      const fieldError = error.details.find(detail => detail.path[0] === name);
      return fieldError ? fieldError.message : null;
  }
  return null;
};

export const generatePtientSchema = (pageLocation) => {
  const isGuestPage = pageLocation === '/open-access-patient';

  return Joi.object({
    patientId:Joi.string().optional().allow(''),
    firstName: Joi.string().required().label('First Name'),
    lastName: Joi.string().required().label('Last Name'),
    claimNumber: Joi.string().required().label('Claim Number'),
    wcbNumber: Joi.string().required().label('WCB Number'),
    dateOfInjury: Joi.date().required().label('Date of Injury'),
    injury: Joi.string().required().label('Injury/Jurisdiction State'),
    gender: Joi.string().valid('female', 'male', 'X', 'Unknown').required().label('Gender'),
    phone1: Joi.string()
      .pattern(/^\d{3}-\d{3}-\d{4}$/)
      .required()
      .messages({
        'string.pattern.base': 'Home Phone number must be in format XXX-XXX-XXXX',
        'string.empty': 'Home Phone number cannot be empty.',
        'any.required': 'Home Phone number is required.'
      })
      .label('Home Phone'),
    phone3: Joi.string().allow('').optional().label('Work Phone'),
    phone4: Joi.string()
      .optional().allow('')
      .label('Ext.'),
    mobile: Joi.string()
      .pattern(/^\d{3}-\d{3}-\d{4}$/)
      .required()
      .messages({
        'string.pattern.base': 'Cell Phone number must be in format XXX-XXX-XXXX',
        'string.empty': 'Cell Phone number cannot be empty',
        'any.required': 'Cell Phone number is required'
      })
      .label('Cell Phone'),
    dateOfBirth: Joi.date().required().label('Date of Birth'),
    email: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .allow('')
      .messages({
        'string.email': 'Please enter a valid email address',
        'string.empty': 'Email is required',
        'any.required': 'Email is required'
      })
      .label('Email'),
    feet: Joi.number().min(0).label('Height (Feet)'),
    inches: Joi.number().min(0).max(11).label('Height (Inches)'),
    weight: Joi.number().allow('').optional().label('Weight (lbs)'),
    alternatePhone: Joi.string().allow('').optional().label('Alternate Phone'),
    alternateDescription: Joi.string().allow('').optional().label('Alternate Description'),
    patient_address1: Joi.string().required().messages({
      'any.required': 'Address 1 is required.'
    }).label('Address 1'),
    patient_address2: Joi.string().optional().allow('').label('Address 2'),
    patient_city: Joi.string().required().label('City'),
    patient_zip: Joi.string().pattern(/^[0-9]{5}(?:-[0-9]{4})?$/).required().label('Zip'),
    patient_state: Joi.string().required().label('State'),
    language:Joi.string().required().label('Language'),
    preferredLanguage: Joi.string().allow('').optional().label('Preferred Language'),
    isTranslationRequired: Joi.boolean().optional().label('Translation Needed'),
    isTransportationRequired: Joi.boolean().optional().label('Transportation Needed'),
    isRush: Joi.boolean().optional().label('Rush Processing'),
    personEmail: Joi.string().email({ tlds: { allow: false } }).optional().label('Person Email').strip().allow(''),
    name: Joi.string().optional().allow(''),
    phone: Joi.string().optional().allow(''),
  })

}

export const EmployeeInformationSchema = Joi.object({
  patientId:Joi.string().optional().allow(''),
  company: Joi.string().required().label('Company Name'),
  companyPhone: Joi.string()
    .pattern(/^\d{10}$|^\d{3}-\d{3}-\d{4}$/) 
    .required()
    .messages({
      'string.pattern.base': 'Company Phone number must be exactly 10 digits.',
      'string.empty': 'Company Phone number cannot be empty.',
      'any.required': 'Company Phone number is required.'
    })
    .label('Company Phone'),
  employer_address1: Joi.string().required().messages({
    'any.required': 'Address 1 is required.'
  }).label('Address 1'),
  employer_address2: Joi.string().optional().allow('').label('Address 2'),
  employer_city: Joi.string().required().label('City'),
  employer_zip: Joi.string().pattern(/^[0-9]{5}(?:-[0-9]{4})?$/).required().label('Zip'),
  employer_state: Joi.string().required().label('State')
});

 export const generateReferredBySchema = (pageLocation) => {
  const isGuestPage = pageLocation === '/open-access-patient';

  return Joi.object({
    patientId:Joi.string().optional().allow(''),
    insuranceSelectValue: Joi.string().required().label('Insurance Company'),
    adjusterSelectValue: isGuestPage ? Joi.string().optional().allow('') : Joi.string().required().label('Adjuster'),
    firstName: Joi.string().required().label('First Name'),
    lastName: Joi.string().required().label('Last Name'),
    practiceName: Joi.string().required().label('Practice Name'),
    phone: Joi.string()
      .pattern(/^\d{10}$|^\d{3}-\d{3}-\d{4}$/) 
      .required()
      .messages({
        'string.pattern.base': 'Phone number must be exactly 10 digits.',
        'string.empty': 'Phone number cannot be empty.',
        'any.required': 'Phone number is required.'
      })
      .label('Phone'),
    email: Joi.string().email({ tlds: { allow: false } }).required().label('Email'),
    fax: Joi.string().allow('').label('Fax'),
    referred_address1: Joi.string().required().messages({
      'any.required': 'Address 1 is required.'
    }).label('Address 1'),
    referred_address2: Joi.string().optional().allow('').label('Address 2'),
    referred_city: Joi.string().required().label('City'),
    referred_zip: Joi.string().pattern(/^[0-9]{5}(?:-[0-9]{4})?$/).required().label('Zip'),
    referred_state: Joi.string().required().label('State'),
    insuredPolicyGroupNumber: Joi.string().required().label('Policy Group or FECA Number'),
    otherClaimId: Joi.string().required().label('Other Claim Id (Designated By NUCC)'),
    claimCodes: Joi.string().required().label('Claim Codes (Designated by NUCC)'),
    patientRelationshipToInsured: Joi.string().required().label('Patient Relationship to Insured'),
    employmentAccident: Joi.string().required().label('Employment'),
    autoAccident: Joi.string().required().label('Auto Accident'),
    otherAccident: Joi.string().required().label('Other Accident'),
    autoAccidentState: Joi.string().optional().allow('').label('Place (State)'),
    referredBy: Joi.string().required().label('Submitted By'),
    referralInfoValue:Joi.string().allow('').optional().label('Referred By'),
    nurseCaseManager:Joi.string().allow('').optional().label('Nurse Case Manager'),
    scripts: Joi.array().items(Joi.string()).optional().allow(''),
  });
};


export const ProceduresSchema = Joi.array().items(
  Joi.object({
    patientId:Joi.string().optional().allow(''),
    procedure: Joi.string().required().label('Procedure'),
    bodyParts: Joi.string().required().label('Body Parts'),
    bodySide: Joi.string().required().label('Body Side'),
    diagnosisRuleOut1: Joi.string().required().label('Diagnosis / Rule Out 1'),
    diagnosisRuleOut2: Joi.string().optional().allow('').label('Diagnosis / Rule Out 2')
  })
);


export const SpecialInstructionsSchema = Joi.object({
  patientId:Joi.string().optional().allow(''),
  instruction1: Joi.string().optional().allow('').label('instruction1'),
  instruction2: Joi.string().optional().allow('').label('instruction2'),
});